import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
// Кнопка «История» + боковое окно с событиями записи.
//
// Единственный способ посмотреть историю в системе: одинаково работает из строки
// грида и с карточки. Дровер выбран потому, что карточка есть не у каждой сущности
// (у заметки, встречи и шаблона её нет), а история нужна у всех.
import { useState, type ReactElement } from 'react';
import { Button, Drawer, Spin, Timeline, Tooltip, Typography } from 'antd';
import { EmptyState } from '../../../components/entity-detail/EmptyState.js';
import { HistoryOutlined } from '@ant-design/icons';

import { formatDateTime } from '../../../shared/format/index.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import type { ActivityEntity } from '../api/activities.client.js';
import { useEntityActivities } from '../hooks/useEntityActivities.js';
import { describeActivity } from '../lib/describeActivity.js';

const { Text } = Typography;

interface EntityHistoryButtonProps {
  entity: ActivityEntity;
  entityId: string;
  /** Заголовок дровера: «История задачи», «История сделки». */
  title: string;
  /** Имя записи под заголовком — чтобы было видно, чья это история. */
  subject?: string | undefined;
  /** В гридах — иконка, на карточках — кнопка с подписью. */
  iconOnly?: boolean;
  size?: 'small' | 'middle';
}

export function EntityHistoryButton({
  entity,
  entityId,
  title,
  subject,
  iconOnly = false,
  size = 'small',
}: EntityHistoryButtonProps): ReactElement {
  useI18nSubscription();
  const [open, setOpen] = useState(false);
  // Запрос уходит только при открытом дровере: в гриде таких кнопок десятки.
  const query = useEntityActivities(entity, open ? entityId : null);
  const activities = query.data ?? [];

  return (
    <>
      {iconOnly ? (
        <Tooltip
          title={i18n.t('crm.features.features.activity.components.entityhistorybutton.история')}
        >
          <Button
            size={size}
            type="text"
            aria-label={title}
            icon={<HistoryOutlined />}
            onClick={(event) => {
              event.stopPropagation();
              setOpen(true);
            }}
          />
        </Tooltip>
      ) : (
        <Button size={size} icon={<HistoryOutlined />} onClick={() => setOpen(true)}>
          {i18n.t('crm.features.features.activity.components.entityhistorybutton.история_2')}
        </Button>
      )}

      {/* Дровер монтируем только открытым: в гриде таких кнопок десятки, и каждая
          держала бы свой скрытый Drawer со своими стилями. */}
      {open ? (
        <Drawer title={title} open onClose={() => setOpen(false)} size={460} destroyOnHidden>
          {subject ? (
            <Text strong className="mb-4 block">
              {subject}
            </Text>
          ) : null}

          {query.isError ? (
            <ErrorState
              title={i18n.t(
                'crm.features.features.activity.components.entityhistorybutton.не_удалось_загрузить_историю',
              )}
              error={query.error}
              onRetry={() => void query.refetch()}
            />
          ) : query.isLoading ? (
            <div className="flex justify-center py-8">
              <Spin />
            </div>
          ) : activities.length === 0 ? (
            <EmptyState
              text={i18n.t(
                'crm.features.features.activity.components.entityhistorybutton.событий_пока_нет',
              )}
            />
          ) : (
            <Timeline
              items={activities.map((activity) => ({
                content: (
                  <div>
                    <div className="text-sm text-gray-900">{describeActivity(activity)}</div>
                    {/* Внутренний ID автора не читается человеком, поэтому из видимой
                        строки он убран и доступен только в подсказке title. Событие
                        от воркера («system») подписано локализованно. */}
                    <div
                      className="text-xs text-gray-500"
                      title={activity.actor === 'system' ? undefined : activity.actor}
                    >
                      {formatDateTime(activity.occurredAt ?? activity.createdAt)}
                      {activity.actor === 'system'
                        ? ` · ${i18n.t('crm.features.activity.actor.system')}`
                        : ''}
                    </div>
                  </div>
                ),
              }))}
            />
          )}
        </Drawer>
      ) : null}
    </>
  );
}
